/**
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add src\helpers\getOfferExpiry.ts to describe how long an offer has left, from OFFER_APPLICABLE_END_TS.
 */

import type { PeppermillBadgeVariant } from '@framework/components/PeppermillBadge.vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import { getDateFromEngage } from '@/helpers/getdateFromEngage';
import isOfferRedeemed from '@/helpers/isOfferRedeemed';

export type OfferExpiryUrgency = 'expired' | 'today' | 'soon' | 'later' | 'none';

export interface OfferExpiry {
  //False when the offer carries no readable end date, or has already been redeemed - a redeemed offer is finished, so
  //counting down to its expiry only adds noise.
  hasExpiry: boolean;
  date: Date | null;
  //Whole days from today. 0 is today, negative is past.
  daysRemaining: number | null;
  urgency: OfferExpiryUrgency;
  //'Expires today', 'Expires in 3 days', 'Expires Sun, Nov 2'.
  label: string;
  //Material Icons ligature for the badge.
  icon: string;
}

const NONE: OfferExpiry = {
  hasExpiry: false,
  date: null,
  daysRemaining: null,
  urgency: 'none',
  label: '',
  icon: '',
};

//Compared at day granularity: a guest thinks of an offer as lasting through its last day, not to the minute.
function getWholeDaysUntil(date: Date): number {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const startOfTarget = new Date(date.getTime());
  startOfTarget.setHours(0, 0, 0, 0);

  return Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);
}

export default function getOfferExpiry(offer: OpcOffer, soonThresholdDays: number = 3): OfferExpiry {
  if (isOfferRedeemed(offer)) {
    return NONE;
  }

  const date = offer.OFFER_APPLICABLE_END_TS ? getDateFromEngage(offer.OFFER_APPLICABLE_END_TS) : null;
  if (!date) {
    return NONE;
  }

  const daysRemaining = getWholeDaysUntil(date);

  let urgency: OfferExpiryUrgency = 'later';
  let label =
    'Expires ' +
    new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
  let icon = 'schedule';

  if (daysRemaining < 0) {
    urgency = 'expired';
    label = 'Expired';
    icon = 'event_busy';
  } else if (daysRemaining === 0) {
    urgency = 'today';
    label = 'Expires today';
    icon = 'priority_high';
  } else if (daysRemaining <= soonThresholdDays) {
    urgency = 'soon';
    label = daysRemaining === 1 ? 'Expires tomorrow' : 'Expires in ' + daysRemaining + ' days';
    icon = 'schedule';
  }

  return { hasExpiry: true, date, daysRemaining, urgency, label, icon };
}

/**
 * The badge variant for each urgency, using the semantic --status-* tokens. 'later' is deliberately neutral: most
 * offers are weeks out, and colouring all of them would leave nothing for the ones that actually need attention.
 */
export const offerExpiryVariants: { [key in OfferExpiryUrgency]: PeppermillBadgeVariant } = {
  expired: 'neutral',
  today: 'danger',
  soon: 'warning',
  later: 'neutral',
  none: 'neutral',
};
